import React, { useState, useEffect } from "react";
import InputGenerico from "../inputGenerico/inputGenerico";
import SelectGenerico from "../selectGenerico/selectGenerico";
import { getTodasCidades } from "../../services/apiCidade";
import { getEnderecoByCEP } from "../../services/apiUsuario";

interface AddressFormValues {
  cdCidade: string;
  dsCep: string;
  nmLogradouro: string;
  nrLogradouro: string;
  nmBairro: string;
  dmResidencial: boolean;
  cidade: string;
  nmComplemento: string;
}

interface Props {
  formValues: AddressFormValues;
  validacaoAtiva: boolean;
  handleFieldChange: (name: string, value: string | number | boolean) => void;
  handleMaskChange: (name: string, value: string) => void;
  handleFieldBlur: (name: string, value: string) => void;
  handleNumberChange: (name: string, value: string) => void;
}

const CamposEndereco: React.FC<Props> = ({
  formValues = {} as AddressFormValues,
  validacaoAtiva,
  handleFieldChange,
  handleMaskChange,
  handleFieldBlur,
  handleNumberChange,
}) => {
  const [ordem, setOrdem] = useState<string>("asc");
  const [cidades, setCidades] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(true);

  const fetchCidades = async () => {
    try {
      const response = await getTodasCidades();
      setCidades(response);
      setLoading(false);
    } catch (error) {
      setLoading(false);
    }
  };

  const fetchAddressInfo = async (cep: string): Promise<any> => {
    try {
      const response = await getEnderecoByCEP(cep);
      return response; 
    } catch (error) {
      return null; 
    }
  };

  const handleCEPChange = async (value: string) => {
    const onlyNumbers = value.replace(/\D/g, "");
    formValues.dsCep = onlyNumbers;
    
    if (onlyNumbers.length <= 8) {
      handleMaskChange("dsCep", onlyNumbers);
    } else {
      handleMaskChange("dsCep", onlyNumbers.slice(0, 5) + "-" + onlyNumbers.slice(5));
    }

    if (onlyNumbers.length === 8) {
      try {
        const response = await fetchAddressInfo(onlyNumbers);
        if (!response.erro) {
          const { logradouro, bairro, localidade } = response;
          handleFieldChange("nmLogradouro", logradouro);
          formValues.nmLogradouro = logradouro;
          handleFieldChange("nmBairro", bairro);
          formValues.nmBairro = bairro;
          const cidadeExiste = cidades.find(
            (cidade) => cidade.nmCidade === localidade
          );
          if (cidadeExiste) {
            handleFieldChange("cdCidade", cidadeExiste.cdCidade);
          }
        }
      } catch (error) {
        console.error(error);
      }
    }
  };

  useEffect(() => {
    fetchCidades();
  }, []);
  return (
    <div className="container">
      <div className="row">
        <div className="col">
          <InputGenerico
            name="dsCep"
            value={formValues.dsCep || ""}
            onChange={(event: { target: { name: string; value: string } }) =>
              handleCEPChange(event.target.value)
            }
            onBlur={(event: { target: { name: string; value: string } }) =>
              handleFieldBlur(event.target.name, event.target.value)
            }
            label="CEP:"
            placeholder="Digite seu CEP..."
            className={`form-control mb-4 `}
            validacaoAtiva={validacaoAtiva}
            type="text"
            maxLength={9}
          />
          <InputGenerico
            name="nmLogradouro"
            value={formValues.nmLogradouro || ""}
            onChange={(event: {
              target: {
                name: string;
                value: string | number | boolean;
              };
            }) => handleFieldChange(event.target.name, event.target.value)}
            label="Rua*:"
            placeholder="Digite o nome da rua..."
            className={`form-control mb-4 ${
              !(formValues.nmLogradouro?.length > 0) && validacaoAtiva
                ? "input-invalid"
                : ""
            }`}
            validacaoAtiva={validacaoAtiva}
            type="text"
          />
          <InputGenerico
            name="nrLogradouro"
            value={formValues.nrLogradouro || ""}
            onChange={(event: { target: { name: string; value: string } }) =>
              handleNumberChange(event.target.name, event.target.value)
            }
            label="Número*:"
            placeholder="Digite o número da residência..."
            className={`form-control mb-4 ${
              !(formValues.nrLogradouro?.length > 0) && validacaoAtiva
                ? "input-invalid"
                : ""
            }`}
            validacaoAtiva={validacaoAtiva}
            type="text"
            min={0}
            maxLength={10}
          />
          <div className="d-flex gap-2 mt-2">
            <label htmlFor="dmResidencial">Endereço residencial?</label>
            <div>
              <input
                id="residencialSim"
                name="dmResidencial"
                value="sim"
                checked={formValues.dmResidencial === true}
                onChange={(event) => handleFieldChange(event.target.name, true)}
                type="radio"
                className="me-1"
              />
              <label htmlFor="residencialSim">Sim</label>
            </div>
            <div>
              <input
                id="residencialNao"
                name="dmResidencial"
                value="nao"
                checked={formValues.dmResidencial === false}
                onChange={(event) =>
                  handleFieldChange(event.target.name, false)
                }
                type="radio"
                className="me-1"
              />
              <label htmlFor="residencialNao">Não</label>
            </div>
          </div>
        </div>
        <div className="col">
          <InputGenerico
            name="nmBairro"
            value={formValues.nmBairro || ""}
            onChange={(event: { target: { name: string; value: string } }) =>
              handleFieldChange(event.target.name, event.target.value)
            }
            label="Bairro*:"
            placeholder="Digite o bairro..."
            className={`form-control mb-4 ${
              !(formValues.nmBairro?.length > 0) && validacaoAtiva
                ? "input-invalid"
                : ""
            }`}
            validacaoAtiva={validacaoAtiva}
            type="text"
            maxLength={100}
          />
          <SelectGenerico
            opcoes={cidades.map((cidade: { cdCidade: any; nmCidade: any }) => ({
              valor: cidade.cdCidade,
              rotulo: cidade.nmCidade,
            }))}
            valorSelecionado={formValues.cdCidade || ""}
            onChange={(value: string | number) =>
              handleFieldChange("cdCidade", value)
            }
            label="Cidade*:"
            mensagemError="Não há cidades a serem listadas"
            opcaoPadrao="Selecione a cidade..."
            classe={`selectUf form-select mb-3 ${
              !formValues.cdCidade && validacaoAtiva ? "select-invalid" : ""
            }`}
            classPadrao="corOption"
            validacaoAtiva={validacaoAtiva}
          />
          <InputGenerico
            name="nmComplemento"
            value={formValues.nmComplemento || ""}
            onChange={(event: { target: { name: string; value: string } }) =>
              handleFieldChange(event.target.name, event.target.value)
            }
            label="Complemento:"
            placeholder="Ex.: casa, apartamento..."
            className={`form-control mb-4 `}
            validacaoAtiva={validacaoAtiva}
            rows={2}
            maxLength={100}
            type="textarea"
          />
        </div>
      </div>
    </div>
  );
};
export default CamposEndereco;
